import { useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { ConnectionBanner } from './components/ui/ConnectionBanner';
import { Login } from './routes/Login';
import { useSync } from './hooks/useSync';
import { TabsLayout } from './layouts/TabsLayout';
import { UpdateToast } from './components/ui/UpdateToast';
import { AddProduct } from './routes/AddProduct';
import { EditProduct } from './routes/EditProduct';
import { Cart } from './routes/Cart';
import { CustomerDetail } from './routes/CustomerDetail';
import { Debt } from './routes/Debt';
import { Done } from './routes/Done';
import { Expense } from './routes/Expense';
import { Gallery } from './routes/Gallery';
import { Home } from './routes/Home';
import { More } from './routes/More';
import { OrderDetail } from './routes/OrderDetail';
import { Orders } from './routes/Orders';
import { PL } from './routes/PL';
import { Purchase } from './routes/Purchase';
import { PurchaseDone } from './routes/PurchaseDone';
import { Purchases } from './routes/Purchases';
import { PurchaseDetail } from './routes/PurchaseDetail';
import { Receive } from './routes/Receive';
import { ReceiveDone } from './routes/ReceiveDone';
import { Report } from './routes/Report';
import { Sell } from './routes/Sell';
import { Settings } from './routes/Settings';
import { Splash, useSplash } from './routes/Splash';
import { Stock } from './routes/Stock';
import { initBridge, reportDepth } from './bridge';
import { useViewportHeight } from './hooks/useNav';

/** Paths that live under the tab bar; everything else is a pushed screen. */
const TAB_ROUTES = new Set(['/', '/sell', '/debt', '/more']);

/**
 * Route table. Paths mirror the expo-router file names one-for-one, so the
 * mobile app's navigation model carries over unchanged.
 */
export function App() {
  const location = useLocation();
  const splash = useSplash();
  const live = useSync();
  useViewportHeight();

  useEffect(() => {
    initBridge();
  }, []);

  // Keeps the shell's hardware back button honest: 0 at a tab root means Android
  // exits rather than trapping the user on Home.
  useEffect(() => {
    reportDepth(TAB_ROUTES.has(location.pathname) ? 0 : 1);
  }, [location.pathname]);

  // The gate. Nothing renders until somebody is signed in — every screen reads
  // shop data, and there is none to read before that.
  if (live.status === 'unauthorised') return <Login />;

  return (
    <>
      <ConnectionBanner />
      <Routes>
        <Route element={<TabsLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/sell" element={<Sell />} />
          <Route path="/debt" element={<Debt />} />
          <Route path="/more" element={<More />} />
        </Route>

        <Route path="/cart" element={<Cart />} />
        <Route path="/done" element={<Done />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/orders/:id" element={<OrderDetail />} />
        <Route path="/expense" element={<Expense />} />
        <Route path="/purchase" element={<Purchase />} />
        <Route path="/purchase-done" element={<PurchaseDone />} />
        <Route path="/purchases" element={<Purchases />} />
        <Route path="/purchases/:id" element={<PurchaseDetail />} />
        <Route path="/stock" element={<Stock />} />
        <Route path="/report" element={<Report />} />
        <Route path="/pl" element={<PL />} />
        <Route path="/add-product" element={<AddProduct />} />
        <Route path="/product/:id" element={<EditProduct />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/customer/:id" element={<CustomerDetail />} />
        <Route path="/receive/:id" element={<Receive />} />
        <Route path="/receive-done" element={<ReceiveDone />} />

        {import.meta.env.DEV ? <Route path="/__gallery" element={<Gallery />} /> : null}

        <Route path="*" element={<Home />} />
      </Routes>

      <UpdateToast />
      {splash.visible ? <Splash onSkip={splash.skip} /> : null}
    </>
  );
}
